import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  doc,
  where,
  orderBy,
} from "firebase/firestore";
import {
  ChartBarIcon,
  UsersIcon,
  UserGroupIcon,
  BookOpenIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

function Stats() {
  // eslint-disable-next-line no-unused-vars
  const [organizations, setOrganizations] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const orgsRef = collection(db, "organizations");
      const q = query(orgsRef, orderBy("name"));
      const snapshot = await getDocs(q);

      const orgsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setOrganizations(orgsList);
      await fetchStatsForOrganizations(orgsList);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      toast.error("Error loading organizations");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatsForOrganizations = async (orgsList) => {
    // Convert date strings to JavaScript Date objects for comparison
    let startDateObj = null;
    let endDateObj = null;

    if (startDate) {
      startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0); // Start of day
    }

    if (endDate) {
      endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999); // End of day
    }

    const stats = [];

    for (const org of orgsList) {
      try {
        const orgRef = doc(db, "organizations", org.id);

        // Get user counts
        const usersRef = collection(db, "users");
        const usersQuery = query(
          usersRef,
          where("organization_id", "==", orgRef)
        );
        const usersSnapshot = await getDocs(usersQuery);

        let totalUsers = 0;
        let totalAdmins = 0;

        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          totalUsers++;
          if (userData.user_type === "admin") {
            totalAdmins++;
          }
        });

        // Get activity counts from user_activity collection
        // Fetch all activities for the organization and filter by date in memory
        // This avoids Firestore composite index requirements
        const activityRef = collection(db, "user_activity");
        const activityQuery = query(
          activityRef,
          where("organization_id", "==", orgRef)
        );
        const activitySnapshot = await getDocs(activityQuery);

        let logins = 0;
        let lessonsAccessed = 0;
        let lessonsCompleted = 0;

        activitySnapshot.forEach((doc) => {
          const activityData = doc.data();
          
          // Check if activity is within date range (if dates are provided)
          let isInDateRange = true;
          if (startDateObj || endDateObj) {
            // Convert Firestore Timestamp to JavaScript Date
            let activityDate;
            if (activityData.created_date) {
              if (typeof activityData.created_date.toDate === 'function') {
                // Firestore Timestamp
                activityDate = activityData.created_date.toDate();
              } else if (activityData.created_date instanceof Date) {
                // Already a Date object
                activityDate = activityData.created_date;
              } else {
                // Try to convert to Date
                activityDate = new Date(activityData.created_date);
              }
            } else {
              // No date, skip this activity
              isInDateRange = false;
            }
            
            if (isInDateRange && activityDate) {
              if (startDateObj && activityDate < startDateObj) {
                isInDateRange = false;
              }
              if (endDateObj && activityDate > endDateObj) {
                isInDateRange = false;
              }
            }
          }

          // Only count activities within the date range
          if (isInDateRange) {
            switch (activityData.type) {
              case "user_login":
                logins++;
                break;
              case "accessed_lesson":
                lessonsAccessed++;
                break;
              case "completed_lesson":
                lessonsCompleted++;
                break;
              default:
                // Ignore unknown activity types
                break;
            }
          }
        });

        stats.push({
          id: org.id,
          name: org.name,
          totalUsers,
          totalAdmins,
          logins,
          lessonsAccessed,
          lessonsCompleted,
        });
      } catch (error) {
        console.error(`Error fetching stats for org ${org.id}:`, error);
        // Add org with zero stats if there's an error
        stats.push({
          id: org.id,
          name: org.name,
          totalUsers: 0,
          totalAdmins: 0,
          logins: 0,
          lessonsAccessed: 0,
          lessonsCompleted: 0,
        });
      }
    }

    setStatsData(stats);
  };

  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      await fetchOrganizations();
      toast.success("Stats refreshed successfully");
    } catch (error) {
      toast.error("Error refreshing stats");
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleDateRangeChange = async () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.error("Start date must be before or equal to end date");
      return;
    }
    setLoading(true);
    try {
      await fetchStatsForOrganizations(organizations);
      toast.success("Stats filtered successfully");
    } catch (error) {
      toast.error("Error filtering stats");
    } finally {
      setLoading(false);
    }
  };

  const handleClearDates = async () => {
    setStartDate("");
    setEndDate("");
    setLoading(true);
    try {
      await fetchStatsForOrganizations(organizations);
      toast.success("Date filter cleared");
    } catch (error) {
      toast.error("Error clearing filter");
    } finally {
      setLoading(false);
    }
  };

  const getTotalStats = () => {
    return statsData.reduce(
      (totals, org) => ({
        totalUsers: totals.totalUsers + org.totalUsers,
        totalAdmins: totals.totalAdmins + org.totalAdmins,
        totalLogins: totals.totalLogins + org.logins,
        totalLessonsAccessed: totals.totalLessonsAccessed + org.lessonsAccessed,
        totalLessonsCompleted:
          totals.totalLessonsCompleted + org.lessonsCompleted,
      }),
      {
        totalUsers: 0,
        totalAdmins: 0,
        totalLogins: 0,
        totalLessonsAccessed: 0,
        totalLessonsCompleted: 0,
      }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading statistics...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totals = getTotalStats();

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold text-primary-600">
                  Statistics Dashboard
                </h1>
                <p className="text-gray-600 mt-1">
                  Overview of all organizations and user activity
                </p>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshLoading}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <ChartBarIcon className="w-4 h-4" />
                <span>
                  {refreshLoading ? "Refreshing..." : "Refresh Stats"}
                </span>
              </button>
            </div>

            {/* Date Range Filter */}
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDateRangeChange}
                    disabled={loading || (!startDate && !endDate)}
                    className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply Filter
                  </button>
                  {(startDate || endDate) && (
                    <button
                      onClick={handleClearDates}
                      disabled={loading}
                      className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              {(startDate || endDate) && (
                <p className="mt-2 text-sm text-gray-600">
                  Filtering activity from{" "}
                  {startDate || "beginning"} to {endDate || "now"}
                </p>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <UsersIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Users
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totals.totalUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <UserGroupIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Admins
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totals.totalAdmins}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <BookOpenIcon className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Logins
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totals.totalLogins}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BookOpenIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Lessons Accessed
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totals.totalLessonsAccessed}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Lessons Completed
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totals.totalLessonsCompleted}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Organizations Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Organization Statistics
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Detailed breakdown by organization
              </p>
            </div>
            <div className="border-t border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admins
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Logins
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lessons Accessed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lessons Completed
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {statsData.map((org) => (
                    <tr key={org.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {org.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {org.totalUsers}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {org.totalAdmins}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {org.logins}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {org.lessonsAccessed}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {org.lessonsCompleted}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {statsData.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No organizations found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Stats;
